import type { JSX } from 'react';

/**
 * A deliberately tiny Markdown subset renderer.
 *
 * No library: this renders a legal disclosure, and pulling in a general Markdown
 * parser would mean auditing its HTML-injection behaviour for a document that
 * must be exactly what we wrote. This handles only what the disclosure uses —
 * headings, bold, tables, lists, rules, paragraphs — and emits React elements,
 * never HTML strings, so there is no innerHTML path to get wrong.
 *
 * Showing the raw source instead is not a neutral fallback: a disclosure full of
 * `##` and `**` reads as unfinished, which works against the "clear and
 * conspicuous" standard in 15 U.S.C. § 7001(c)(1)(B).
 */

/**
 * Bold and italic.
 *
 * Emphasis is matched AFTER lines have been joined into blocks, never per source
 * line — the disclosure hard-wraps at 100 columns, so a `**...**` span routinely
 * straddles a line break. Matching per line left a literal `**` mid-sentence in
 * a legal document, which a browser test caught.
 */
function inline(text: string, keyBase: string): (string | JSX.Element)[] {
  const out: (string | JSX.Element)[] = [];
  const re = /\*\*([\s\S]+?)\*\*|\*([^*\n][\s\S]*?)\*/g;
  let last = 0, m: RegExpExecArray | null, i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1] !== undefined) out.push(<strong key={`${keyBase}-b${i++}`}>{m[1]}</strong>);
    else out.push(<em key={`${keyBase}-i${i++}`}>{m[2]}</em>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Markdown({ source }: { source: string }) {
  const lines = source.split('\n');
  const blocks: JSX.Element[] = [];
  let para: string[] = [];
  let list: string[] = [];
  let table: string[][] = [];
  let k = 0;

  const flushPara = () => {
    if (para.length === 0) return;
    blocks.push(<p key={`p${k++}`} style={{ margin: '0 0 .85rem' }}>{inline(para.join(' '), `p${k}`)}</p>);
    para = [];
  };
  const flushList = () => {
    if (list.length === 0) return;
    blocks.push(
      <ul key={`u${k++}`} style={{ margin: '0 0 .85rem', paddingLeft: '1.25rem' }}>
        {list.map((li, j) => <li key={j} style={{ marginBottom: '.25rem' }}>{inline(li, `l${k}-${j}`)}</li>)}
      </ul>
    );
    list = [];
  };
  const flushTable = () => {
    if (table.length === 0) return;
    const [head, ...rows] = table;
    blocks.push(
      <div key={`t${k++}`} style={{ overflowX: 'auto', margin: '0 0 .85rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.9em' }}>
          <thead><tr>{head!.map((c, j) => (
            <th key={j} style={{ textAlign: 'left', padding: '.4rem .5rem', borderBottom: '2px solid var(--line)' }}>
              {inline(c, `th${j}`)}
            </th>
          ))}</tr></thead>
          <tbody>{rows.map((r, ri) => (
            <tr key={ri}>{r.map((c, j) => (
              <td key={j} style={{ padding: '.4rem .5rem', borderBottom: '1px solid var(--line)', verticalAlign: 'top' }}>
                {inline(c, `td${ri}-${j}`)}
              </td>
            ))}</tr>
          ))}</tbody>
        </table>
      </div>
    );
    table = [];
  };
  const flushAll = () => { flushPara(); flushList(); flushTable(); };

  for (const raw of lines) {
    const line = raw.trimEnd();

    if (/^\s*$/.test(line)) { flushAll(); continue; }

    if (/^\|/.test(line)) {
      flushPara(); flushList();
      const cells = line.split('|').slice(1, -1).map((c) => c.trim());
      if (cells.every((c) => /^-{2,}$/.test(c.replace(/:/g, '')))) continue; // separator row
      table.push(cells);
      continue;
    }
    flushTable();

    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      flushAll();
      const level = h[1]!.length;
      const size = level === 1 ? '1.35rem' : level === 2 ? '1.08rem' : '.98rem';
      blocks.push(
        <div key={`h${k++}`} style={{ fontSize: size, fontWeight: 600, margin: '1.4rem 0 .5rem' }}>
          {inline(h[2]!, `h${k}`)}
        </div>
      );
      continue;
    }

    if (/^-{3,}$/.test(line)) {
      flushAll();
      blocks.push(<hr key={`r${k++}`} style={{ border: 0, borderTop: '1px solid var(--line)', margin: '1.2rem 0' }} />);
      continue;
    }

    const li = /^[-*]\s+(.*)$/.exec(line);
    if (li) { flushPara(); list.push(li[1]!); continue; }

    // A non-blank line while a list is open is a WRAPPED continuation of the
    // last item, not a new paragraph. Treating it as a paragraph split bold
    // spans across the boundary and produced stray asterisks.
    if (list.length > 0 && para.length === 0) {
      list[list.length - 1] += ' ' + line.trim();
      continue;
    }
    flushList();

    para.push(line);
  }
  flushAll();

  return <div style={{ fontSize: '.92rem', lineHeight: 1.6 }}>{blocks}</div>;
}
